import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { ticketsAPI, searchAPI, usersAPI } from '../../services/api';
import './TicketNew.css';

const DEBOUNCE_MS = 320;

function useDebounced(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

const TicketNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [remarks, setRemarks] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [assignedTo, setAssignedTo] = useState('');
  const [studentQ, setStudentQ] = useState('');
  const [agencyQ, setAgencyQ] = useState('');
  const debouncedStudent = useDebounced(studentQ, DEBOUNCE_MS);
  const debouncedAgency = useDebounced(agencyQ, DEBOUNCE_MS);
  const [studentHits, setStudentHits] = useState([]);
  const [agencyHits, setAgencyHits] = useState([]);
  const [studentSel, setStudentSel] = useState(null);
  const [agencySel, setAgencySel] = useState(null);
  const [team, setTeam] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (debouncedStudent.trim().length < 1) {
        setStudentHits([]);
        return;
      }
      try {
        const res = await searchAPI.students(debouncedStudent.trim());
        if (!cancelled) setStudentHits(res.data || []);
      } catch {
        if (!cancelled) setStudentHits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedStudent]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (debouncedAgency.trim().length < 1) {
        setAgencyHits([]);
        return;
      }
      try {
        const res = await searchAPI.agencies(debouncedAgency.trim());
        if (!cancelled) setAgencyHits(res.data || []);
      } catch {
        if (!cancelled) setAgencyHits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedAgency]);

  useEffect(() => {
    (async () => {
      try {
        const res = await usersAPI.getSalesTeam();
        setTeam(res.data || []);
      } catch {
        setTeam([]);
      }
    })();
  }, []);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!title.trim()) {
        toast.error(t('tickets.toasts.titleRequired'));
        return;
      }
      setSubmitting(true);
      try {
        const body = {
          title: title.trim(),
          priority,
          student_id: studentSel?.student_id || null,
          agency_id: agencySel?.agency_id || null,
          assigned_to: assignedTo ? parseInt(assignedTo, 10) : null,
          remarks: remarks.trim() || undefined
        };
        const res = await ticketsAPI.create(body);
        toast.success(t('tickets.toasts.created'));
        navigate(`/tickets/${res.data.ticket_id}`);
      } catch (err) {
        toast.error(err.response?.data?.message || t('tickets.toasts.createFailed'));
      } finally {
        setSubmitting(false);
      }
    },
    [title, remarks, priority, studentSel, agencySel, assignedTo, navigate, t]
  );

  return (
    <div className="page-container ticket-new-page">
      <Link to="/tickets" className="ticket-new-back">
        <FiArrowLeft size={18} />
        {t('tickets.backToList')}
      </Link>

      <div className="page-header">
        <h1>{t('tickets.newTicket')}</h1>
      </div>

      <div className="card ticket-new-card">
        <form onSubmit={submit}>
          <div className="ticket-new-section">
            <h2 className="ticket-new-section-title">{t('tickets.sectionDetails')}</h2>

            <div className="ticket-new-stack">
              <div className="ticket-new-grid ticket-new-grid--title-priority">
                <div className="form-group ticket-new-field-grow">
                  <label className="form-label" htmlFor="ticket-new-title">
                    {t('tickets.fields.title')} *
                  </label>
                  <input
                    id="ticket-new-title"
                    className="form-control"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="form-group ticket-new-field-priority">
                  <label className="form-label" htmlFor="ticket-new-priority">
                    {t('tickets.fields.priority')}
                  </label>
                  <select
                    id="ticket-new-priority"
                    className="form-control"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="LOW">{t('tickets.priority.low', 'LOW')}</option>
                    <option value="MEDIUM">{t('tickets.priority.medium', 'MEDIUM')}</option>
                    <option value="HIGH">{t('tickets.priority.high', 'HIGH')}</option>
                  </select>
                </div>
              </div>

              <div className="form-group ticket-new-field-full">
                <label className="form-label" htmlFor="ticket-new-assignee">
                  {t('tickets.fields.assignee')}
                </label>
                <select
                  id="ticket-new-assignee"
                  className="form-control"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                >
                  <option value="">{t('tickets.unassigned')}</option>
                  {team.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name} ({u.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group ticket-new-field-full">
                <label className="form-label" htmlFor="ticket-new-remarks">
                  {t('tickets.fields.remarks')}
                </label>
                <textarea
                  id="ticket-new-remarks"
                  className="form-control ticket-new-remarks"
                  rows={5}
                  placeholder={t('tickets.fields.remarksPlaceholder')}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ticket-new-section">
            <h2 className="ticket-new-section-title">{t('tickets.sectionTagging')}</h2>

            <div className="ticket-new-grid ticket-new-grid--tagging">
              <div className="form-group ticket-new-field-wrap">
                <label className="form-label" htmlFor="ticket-new-student-q">
                  {t('tickets.fields.tagStudent')}
                </label>
                <input
                  id="ticket-new-student-q"
                  className="form-control"
                  placeholder={t('tickets.searchPlaceholder')}
                  value={studentQ}
                  onChange={(e) => {
                    setStudentQ(e.target.value);
                    if (studentSel) setStudentSel(null);
                  }}
                  autoComplete="off"
                />
                {studentSel && (
                  <div className="ticket-new-tag-row">
                    <span className="badge badge-info">
                      {studentSel.first_name} {studentSel.last_name} — {studentSel.student_ref}
                      {studentSel.email ? ` · ${studentSel.email}` : ''}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => {
                        setStudentSel(null);
                        setStudentQ('');
                      }}
                    >
                      {t('common.clear')}
                    </button>
                  </div>
                )}
                {!studentSel && studentHits.length > 0 && (
                  <ul className="ticket-new-search-dropdown">
                    {studentHits.map((s) => (
                      <li key={s.student_id}>
                        <button
                          type="button"
                          className="ticket-new-hit-btn"
                          onClick={() => {
                            setStudentSel(s);
                            setStudentQ('');
                            setStudentHits([]);
                          }}
                        >
                          <strong>
                            {s.first_name} {s.last_name}
                          </strong>
                          <div className="text-muted small">
                            {s.student_ref}
                            {s.email ? ` · ${s.email}` : ''}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group ticket-new-field-wrap">
                <label className="form-label" htmlFor="ticket-new-agency-q">
                  {t('tickets.fields.tagAgency')}
                </label>
                <input
                  id="ticket-new-agency-q"
                  className="form-control"
                  placeholder={t('tickets.searchAgencyPlaceholder')}
                  value={agencyQ}
                  onChange={(e) => {
                    setAgencyQ(e.target.value);
                    if (agencySel) setAgencySel(null);
                  }}
                  autoComplete="off"
                />
                {agencySel && (
                  <div className="ticket-new-tag-row">
                    <span className="badge badge-warning">
                      {agencySel.agency_name || agencySel.ref_number} — {agencySel.ref_number}
                      {agencySel.email ? ` · ${agencySel.email}` : ''}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => {
                        setAgencySel(null);
                        setAgencyQ('');
                      }}
                    >
                      {t('common.clear')}
                    </button>
                  </div>
                )}
                {!agencySel && agencyHits.length > 0 && (
                  <ul className="ticket-new-search-dropdown">
                    {agencyHits.map((a) => (
                      <li key={a.agency_id}>
                        <button
                          type="button"
                          className="ticket-new-hit-btn"
                          onClick={() => {
                            setAgencySel(a);
                            setAgencyQ('');
                            setAgencyHits([]);
                          }}
                        >
                          <strong>{a.agency_name || a.ref_number}</strong>
                          <div className="text-muted small">
                            {a.ref_number}
                            {a.email ? ` · ${a.email}` : ''}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="ticket-new-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('tickets.create')}
            </button>
            <Link to="/tickets" className="btn btn-outline-secondary">
              {t('common.cancel')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketNew;
